/* eslint-disable @typescript-eslint/no-explicit-any */
import { APP_INITIALIZER, inject, Injectable, Injector } from '@angular/core';

import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, take, tap, withLatestFrom } from 'rxjs/operators';

import { EnvironmentConfiguration } from '@rx-applications/clientsite/shared/config/utils';
import { ConfigFacade } from '@rx-applications/clientsite/shared/config/data-access';

import { ErrorService } from '@core/modules/error/services/error.service';

import { ClientsiteSharedLoggingService, InitProperties, LoggingFacade } from '@rx-applications/clientsite/core/logging';
import { ENVIRONMENT_TOKEN } from '@rx-applications/shared/utils';
import { AuthApiFacade } from '@rx-applications/shared/auth/api';

@Injectable()
export class AppConfig {
  readonly #appversion = inject(ENVIRONMENT_TOKEN).version;

  constructor(
    private authFacade: AuthApiFacade,
    private configFacade: ConfigFacade,
    private errorService: ErrorService,
    private injector: Injector,
  ) {}

  load(): Promise<void> {
    /**
     * Before we can bootstrap angular application we have to make sure the follow steps are executed:
     * 1. load env configuration and auth token from locale storage if exists
     * 2. a.) if user is authenticated load base config and user config
     *    b.) if user is NOT authenticated load only base config
     */
    return new Promise((resolve) => {
      const timer = performance.now();
      this.basicConfiguration$().subscribe(
        ({ isAuthenticated, envConfig, envConfigError }) => {
          if (envConfigError) {
            return this.handleError(resolve, envConfigError);
          }

          this.configFacade.loadBaseConfiguration();
          this.setTrustedTypes();

          const clientsiteSharedLoggingService = this.injector.get(ClientsiteSharedLoggingService);
          const loggingFacade = this.injector.get(LoggingFacade);

          if (envConfig && envConfig.authserver.length) {
            this.initializeLogging(envConfig, clientsiteSharedLoggingService);
          }

          if (!isAuthenticated) {
            return this.nonAuthenticatedFlow$(resolve);
          }

          return this.authenticatedFlow$(resolve, loggingFacade, timer);
        },
        (error) => this.handleError(resolve, error),
      );
    });
  }

  private handleError(resolve: () => void, error: string | null) {
    if (error) {
      this.errorService.handleError(new Error(error));
    }

    return resolve();
  }

  private initializeLogging(
    envConfig: EnvironmentConfiguration,
    clientsiteSharedLoggingService: ClientsiteSharedLoggingService,
  ): void {
    clientsiteSharedLoggingService.init({
      environmentTracking: envConfig.tracking,
      appVersion: this.#appversion,
      environment: envConfig.env,
    } as InitProperties);
  }

  private nonAuthenticatedFlow$(resolve: () => void): Subscription {
    return this.configFacade.hasBaseConfigurationHttpCompleted$
      .pipe(
        filter((hasBaseConfigurationHttpCompleted) => hasBaseConfigurationHttpCompleted),
        withLatestFrom(this.configFacade.error$),
        map(([, error]: [unknown, string | null]) => error),
        take(1),
      )
      .subscribe(
        (error: string) => this.handleError(resolve, error),
        (error) => this.handleError(resolve, error),
      );
  }

  private authenticatedFlow$(resolve: () => void, loggingFacade: LoggingFacade, timer: number): Subscription {
    return combineLatest([
      this.configFacade.hasBaseConfigurationHttpCompleted$,
      this.configFacade.hasUserConfigurationHttpCompleted$,
    ])
      .pipe(
        filter(
          ([hasBaseConfigurationHttpCompleted, hasUserConfigurationHttpCompleted]: [boolean, boolean]) =>
            hasBaseConfigurationHttpCompleted && hasUserConfigurationHttpCompleted,
        ),
        tap(() => {
          loggingFacade.fullConfigLoaded({
            time: performance.now() - timer,
          });
        }),
        withLatestFrom(this.configFacade.error$, this.authFacade.error$),
        map(([, baseConfigError, authError]: [unknown, string | null, string | null]) => [baseConfigError, authError]),
        take(1),
      )
      .subscribe(
        ([baseConfigError, authError]: [string, string]) => this.handleError(resolve, baseConfigError || authError),
        (error) => this.handleError(resolve, error),
      );
  }

  private basicConfiguration$(): Observable<{
    isAuthenticated: boolean;
    envConfig: EnvironmentConfiguration;
    envConfigError: string | null;
  }> {
    return combineLatest([
      this.configFacade.hasEnvironmentConfigurationHttpCompleted$,
      this.authFacade.hasLoadTokenFromLocaleStorageCompleted$,
    ]).pipe(
      filter(
        ([hasEnvironmentConfigurationHttpCompleted, hasLoadTokenFromLocaleStorageCompleted]) =>
          hasEnvironmentConfigurationHttpCompleted && hasLoadTokenFromLocaleStorageCompleted,
      ),
      withLatestFrom(
        this.authFacade.isAuthenticated$,
        this.configFacade.environmentConfiguration$,
        this.configFacade.error$,
        (_, isAuthenticated, envConfig, envConfigError) => ({
          isAuthenticated,
          envConfig,
          envConfigError,
        }),
      ),
      take(1),
    );
  }

  private setTrustedTypes(): void {
    if (!(<any>window).trustedTypes) {
      return;
    }
    (<any>window).trustedTypes.createPolicy('default', {
      createScriptURL: (input: string | URL) => {
        return new URL(input).origin === 'https://js.stripe.com' ? input : undefined;
      },
    });
  }
}

// required for AOT
export function loadConfig(config: AppConfig) {
  return () => config.load();
}

export const APP_CONFIG_PROVIDERS = [
  AppConfig,
  {
    provide: APP_INITIALIZER,
    useFactory: loadConfig,
    deps: [AppConfig],
    multi: true,
  },
];
